import React, { useState } from 'react';
import { Handle, Position } from 'reactflow';
import { Clock, Plus, Check, X } from 'lucide-react';
import { Button } from '@/shadcn/components/ui/button';
import { Input } from '@/shadcn/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shadcn/components/ui/select';
import { FlowNodeData } from '../types';
import { cn } from '@/shadcn/lib/utils';

interface DelayNodeV2Props {
  data: FlowNodeData;
}

const formatDelayTime = (durationMs?: number) => {
  if (!durationMs) return 'Not configured';

  const seconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days} ${days === 1 ? 'day' : 'days'}`;
  if (hours > 0) return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  if (minutes > 0) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  return `${seconds} ${seconds === 1 ? 'second' : 'seconds'}`;
};

export const DelayNodeV2: React.FC<DelayNodeV2Props> = ({ data }) => {
  const {
    nodeId,
    metadata,
    isEditing,
    isSelected,
    onNodeClick,
    onDeleteNode,
    onUpdateNode,
  } = data;

  const [isEditingTime, setIsEditingTime] = useState(false);
  const [tempValue, setTempValue] = useState('1');
  const [tempUnit, setTempUnit] = useState('hours');

  const handleNodeClick = () => {
    onNodeClick?.(nodeId);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDeleteNode?.(nodeId);
  };

  const handleStartEditing = (e: React.MouseEvent) => {
    e.stopPropagation();

    // Pre-populate with existing values if available
    const durationMs = metadata?.durationMs;
    if (durationMs) {
      const seconds = Math.floor(durationMs / 1000);
      const minutes = Math.floor(seconds / 60);
      const hours = Math.floor(minutes / 60);
      const days = Math.floor(hours / 24);

      if (days > 0) {
        setTempValue(days.toString());
        setTempUnit('days');
      } else if (hours > 0) {
        setTempValue(hours.toString());
        setTempUnit('hours');
      } else if (minutes > 0) {
        setTempValue(minutes.toString());
        setTempUnit('minutes');
      } else {
        setTempValue(seconds.toString());
        setTempUnit('seconds');
      }
    } else {
      setTempValue('1');
      setTempUnit('hours');
    }

    setIsEditingTime(true);
  };

  const handleDone = () => {
    const value = parseInt(tempValue, 10);
    if (value > 0) {
      let durationMs: number;

      switch (tempUnit) {
        case 'seconds':
          durationMs = value * 1000;
          break;
        case 'minutes':
          durationMs = value * 60 * 1000;
          break;
        case 'hours':
          durationMs = value * 60 * 60 * 1000;
          break;
        case 'days':
          durationMs = value * 24 * 60 * 60 * 1000;
          break;
        default:
          durationMs = value * 60 * 60 * 1000; // Default to hours
      }

      onUpdateNode?.(nodeId, {
        metadata: {
          ...metadata,
          durationMs,
        },
      });
    }

    setIsEditingTime(false);
  };

  const handleCancel = () => {
    setIsEditingTime(false);
    setTempValue('1');
    setTempUnit('hours');
  };

  const durationMs = metadata?.durationMs;
  const isConfigured = durationMs && durationMs > 0;

  return (
    <div className="relative">
      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Top}
        isConnectable={false}
        className="w-3 h-3 bg-gray-400 border-2 border-white"
      />

      {/* Main node container - Figma style */}
      <div
        className={cn(
          'w-64 bg-white border border-gray-200 rounded-2xl shadow-sm cursor-pointer transition-all duration-200 hover:shadow-md hover:border-gray-300',
          isSelected && 'ring-2 ring-blue-500 ring-offset-2 border-blue-300'
        )}
        onClick={handleNodeClick}
      >
        <div className="p-4">
          {/* Header with icon and title */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
                <Clock className="h-4 w-4 text-white" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900 text-sm">Time Delay</h3>
              </div>
            </div>

            {isEditing && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 hover:bg-gray-100"
                onClick={handleDelete}
              >
                <svg className="h-3 w-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </Button>
            )}
          </div>

          {/* Content area */}
          <div className="space-y-3">
            {isEditingTime ? (
              <div className="space-y-3">
                <div className="flex gap-2 items-center">
                  <Input
                    type="number"
                    min="1"
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    className="w-20 h-8 text-sm"
                    placeholder="1"
                  />
                  <Select value={tempUnit} onValueChange={setTempUnit}>
                    <SelectTrigger className="w-24 h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="seconds">Seconds</SelectItem>
                      <SelectItem value="minutes">Minutes</SelectItem>
                      <SelectItem value="hours">Hours</SelectItem>
                      <SelectItem value="days">Days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="default"
                    size="sm"
                    className="flex-1 h-8 text-xs bg-green-500 hover:bg-green-600"
                    onClick={handleDone}
                  >
                    <Check className="h-3 w-3 mr-1" />
                    Done
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 h-8 text-xs"
                    onClick={handleCancel}
                  >
                    <X className="h-3 w-3 mr-1" />
                    Cancel
                  </Button>
                </div>
              </div>
            ) : isConfigured ? (
              <div className="bg-gray-100 rounded-lg px-3 py-2 flex items-center justify-between">
                <span className="text-sm text-gray-700">Wait for {formatDelayTime(durationMs)}</span>
                {isEditing && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 w-5 p-0 hover:bg-gray-200"
                    onClick={handleStartEditing}
                  >
                    <svg className="h-3 w-3 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  </Button>
                )}
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="w-full h-8 border-dashed border-gray-300 text-gray-500 hover:text-gray-700 hover:border-gray-400 bg-transparent hover:bg-gray-50"
                onClick={handleStartEditing}
              >
                <Plus className="h-3 w-3 mr-2" />
                Add Delay Time
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Bottom}
        isConnectable={false}
        className="w-3 h-3 bg-gray-400 border-2 border-white"
      />

      {/* Add button positioned at bottom center */}
      {isEditing && (
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-4 z-20">
          <Button
            variant="outline"
            size="sm"
            className="w-8 h-8 p-0 rounded-full bg-white border border-gray-300 hover:border-blue-400 hover:bg-blue-50 shadow-md"
            onClick={(e) => {
              e.stopPropagation(); // Prevent node selection
              const rect = e.currentTarget.getBoundingClientRect();
              data.onShowOptionBar?.(nodeId, {
                x: rect.left + rect.width / 2,
                y: rect.top
              });
            }}
          >
            <Plus className="h-4 w-4 text-gray-600" />
          </Button>
        </div>
      )}
    </div>
  );
};
